import { TilesError } from '@/plugins/tiles/TilesError'
import type { TilesRuntimeOptions } from '@/plugins/tiles/types'

/** 软件缓存默认值，不代表设备容量或性能保证；宿主可按实测覆盖。 */
export const DEFAULT_TILES_RUNTIME_OPTIONS: TilesRuntimeOptions = Object.freeze({
  maxScreenSpaceError: 16,
  lodHysteresis: 0.15,
  maxCacheBytes: 256 * 1024 * 1024,
  fallbackCacheRatio: 0.2,
  maxConcurrentRequests: 6,
  maxConcurrentDecodes: 2,
  maxRetries: 2,
  retryDelayMs: 500,
  maxHierarchyDepth: 64,
  maxMetadataBytes: 16 * 1024 * 1024,
  maxProbeBytes: 1024 * 1024,
  boundsPolicy: 'declared',
})

export function resolveTilesRuntimeOptions(
  options: Partial<TilesRuntimeOptions> = {},
  base: TilesRuntimeOptions = DEFAULT_TILES_RUNTIME_OPTIONS
): TilesRuntimeOptions {
  const result = { ...base, ...options }
  for (const key of ['lodHysteresis', 'fallbackCacheRatio'] as const) {
    if (!Number.isFinite(result[key]) || result[key] < 0 || result[key] >= 1)
      throw new TilesError({
        code: 'invalid-input',
        stage: 'root',
        message: `Tiles 配置 ${key} 必须在 [0, 1) 范围内`,
      })
  }
  for (const key of [
    'maxScreenSpaceError',
    'maxCacheBytes',
    'maxConcurrentRequests',
    'maxConcurrentDecodes',
    'maxHierarchyDepth',
    'maxMetadataBytes',
    'maxProbeBytes',
  ] as const) {
    if (!Number.isFinite(result[key]) || result[key] <= 0) {
      throw new TilesError({
        code: 'invalid-input',
        stage: 'root',
        message: `Tiles 配置 ${key} 必须为有限正数`,
      })
    }
  }
  for (const key of [
    'maxConcurrentRequests',
    'maxConcurrentDecodes',
    'maxHierarchyDepth',
    'maxCacheBytes',
    'maxMetadataBytes',
    'maxProbeBytes',
  ] as const) {
    if (!Number.isInteger(result[key])) {
      throw new TilesError({
        code: 'invalid-input',
        stage: 'root',
        message: `Tiles 配置 ${key} 必须为整数`,
      })
    }
  }
  if (
    !Number.isInteger(result.maxRetries) ||
    result.maxRetries < 0 ||
    !Number.isFinite(result.retryDelayMs) ||
    result.retryDelayMs < 0 ||
    !['declared', 'conservative'].includes(result.boundsPolicy)
  ) {
    throw new TilesError({ code: 'invalid-input', stage: 'root', message: 'Tiles 运行配置无效' })
  }
  return Object.freeze(result)
}
